import { useEffect, useState } from 'react';
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { locationTypes } from '../../constants/statusOptions';
import { getProjects } from '../../services/projectService';
import { updateLocation } from '../../services/locationService';
import type { LocationWithDetails } from '../../types/location';

interface ProjectOption {
  ProjectKey: string;
  ProjectId: string;
  ProjectName: string;
  SiteKey: string;
}

interface EditLocationManageDialogProps {
  open: boolean;
  location: LocationWithDetails | null;
  onClose: () => void;
  onUpdated: () => void;
}

export const EditLocationManageDialog = ({
  open, location, onClose, onUpdated,
}: EditLocationManageDialogProps) => {
  const [locationId, setLocationId] = useState('');
  const [locationName, setLocationName] = useState('');
  const [locationType, setLocationType] = useState('Borehole');
  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(null);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (location && open) {
      setLocationId(location.LocationId);
      setLocationName(location.LocationName);
      setLocationType(location.LocationType);
      setError('');
      getProjects({ pageSize: 500, sortBy: 'ProjectName', sortDir: 'asc' })
        .then((res) => {
          const opts = res.data.map((p) => ({
            ProjectKey: p.ProjectKey, ProjectId: p.ProjectId,
            ProjectName: p.ProjectName, SiteKey: p.SiteKey,
          }));
          setProjects(opts);
          const current = opts.find((p) => p.ProjectKey === location.ProjectKey);
          setSelectedProject(current ?? null);
        })
        .catch(() => {});
    }
  }, [location, open]);

  const handleSubmit = async () => {
    if (!location) return;
    if (!locationId.trim()) { setError('Location ID is required'); return; }
    if (!locationName.trim()) { setError('Location Name is required'); return; }
    if (!selectedProject) { setError('Project is required'); return; }
    setSubmitting(true);
    setError('');
    try {
      await updateLocation(location.LocationKey, {
        LocationId: locationId.trim(),
        LocationName: locationName.trim(),
        LocationType: locationType,
        ProjectKey: selectedProject.ProjectKey,
        SiteKey: selectedProject.SiteKey,
      });
      onUpdated();
    } catch {
      setError('Failed to update location.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!location) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Edit Location</DialogTitle>
      <Divider />
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5 }}>
        <TextField size="small" label="Location ID" required value={locationId}
          onChange={(e) => setLocationId(e.target.value)} />
        <TextField size="small" label="Location Name" required value={locationName}
          onChange={(e) => setLocationName(e.target.value)} />
        <TextField size="small" select label="Location Type" required value={locationType}
          onChange={(e) => setLocationType(e.target.value)}>
          {locationTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <Autocomplete size="small" options={projects}
          getOptionLabel={(opt) => `${opt.ProjectId} — ${opt.ProjectName}`}
          value={selectedProject}
          onChange={(_e, val) => setSelectedProject(val)}
          isOptionEqualToValue={(opt, val) => opt.ProjectKey === val.ProjectKey}
          renderInput={(params) => (
            <TextField {...params} label="Project" required
              helperText="Moving to a different project also updates the site" />
          )} />
        {error && <Typography variant="body2" color="error">{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}
          sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 1.5 }}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};
